import * as Rox from 'rox-browser';

export const ROLLOUT_FETCHER_STATUS = {
  FROM_NETWORK: 'APPLIED_FROM_NETWORK',
  FROM_CACHE: 'APPLIED_FROM_CACHE',
};

const DEFAULT_FEATURE_FLAGS = {
  maintenanceModeHB: false,
  accountManagerHB: false,
  isAccountManagerEnableToLegacy: false,
  loadingDocumentsHB: false,
  newUrlTechRules: false,
  tradingTransfer: false,
  smartLookHB: false,
  positionPollTimer: 30,
  enableGeolocationOperations: false,
  priceAlerts: false,
  tmpExternalFundsOperable: false,
  tmpPermanenceRule: false,
  tradingUsaOperation: true,
  tmpShowUsaCapitalMovements: false,
  ujetChatHB: true,
  helpChat: true,
  tmpShowHistoricalYieldTusa: true,
  news: false,
  noFeesTrading: false,
  infoBannerContent: '',
  deleteBankAccounts: false,
  bankAccountsLimit: 1,
  authMfaCheck: false,
  tmpEnableNewWL: false,
  realTimeMinAmount: 100,
};

export type FeatureFlags = typeof DEFAULT_FEATURE_FLAGS;

export const featureFlags = {
  maintenanceModeHB: new Rox.Flag(DEFAULT_FEATURE_FLAGS.maintenanceModeHB),
  accountManagerHB: new Rox.Flag(DEFAULT_FEATURE_FLAGS.accountManagerHB),
  isAccountManagerEnableToLegacy: new Rox.Flag(
    DEFAULT_FEATURE_FLAGS.isAccountManagerEnableToLegacy,
  ),
  loadingDocumentsHB: new Rox.Flag(DEFAULT_FEATURE_FLAGS.loadingDocumentsHB),
  newUrlTechRules: new Rox.Flag(DEFAULT_FEATURE_FLAGS.newUrlTechRules),
  tradingTransfer: new Rox.Flag(DEFAULT_FEATURE_FLAGS.tradingTransfer),
  smartLookHB: new Rox.Flag(DEFAULT_FEATURE_FLAGS.smartLookHB),
  positionPollTimer: new Rox.RoxNumber(DEFAULT_FEATURE_FLAGS.positionPollTimer),
  enableGeolocationOperations: new Rox.Flag(
    DEFAULT_FEATURE_FLAGS.enableGeolocationOperations,
  ),
  priceAlerts: new Rox.Flag(DEFAULT_FEATURE_FLAGS.priceAlerts),
  tmpExternalFundsOperable: new Rox.Flag(
    DEFAULT_FEATURE_FLAGS.tmpExternalFundsOperable,
  ),
  tmpPermanenceRule: new Rox.Flag(DEFAULT_FEATURE_FLAGS.tmpPermanenceRule),
  tradingUsaOperation: new Rox.Flag(DEFAULT_FEATURE_FLAGS.tradingUsaOperation),
  tmpShowUsaCapitalMovements: new Rox.Flag(
    DEFAULT_FEATURE_FLAGS.tmpShowUsaCapitalMovements,
  ),
  ujetChatHB: new Rox.Flag(DEFAULT_FEATURE_FLAGS.ujetChatHB),
  helpChat: new Rox.Flag(DEFAULT_FEATURE_FLAGS.helpChat),
  tmpShowHistoricalYieldTusa: new Rox.Flag(
    DEFAULT_FEATURE_FLAGS.tmpShowHistoricalYieldTusa,
  ),
  news: new Rox.Flag(DEFAULT_FEATURE_FLAGS.news),
  noFeesTrading: new Rox.Flag(DEFAULT_FEATURE_FLAGS.noFeesTrading),
  infoBannerContent: new Rox.RoxString(DEFAULT_FEATURE_FLAGS.infoBannerContent),
  deleteBankAccounts: new Rox.Flag(DEFAULT_FEATURE_FLAGS.deleteBankAccounts),
  bankAccountsLimit: new Rox.RoxNumber(DEFAULT_FEATURE_FLAGS.bankAccountsLimit),
  authMfaCheck: new Rox.Flag(DEFAULT_FEATURE_FLAGS.authMfaCheck),
  tmpEnableNewWL: new Rox.Flag(DEFAULT_FEATURE_FLAGS.authMfaCheck),
  realTimeMinAmount: new Rox.RoxNumber(DEFAULT_FEATURE_FLAGS.realTimeMinAmount),
};

export const initRollout = async (
  apiKey: string,
  customProperties?: {
    [key: string]: any;
  },
  configurationFetchedHandler?: (fetcherResult: Rox.RoxFetcherResult) => void,
) => {
  try {
    customProperties &&
      Object.keys(customProperties).forEach((key) => {
        Rox.setCustomStringProperty(key, customProperties[key]);
      });
    Rox.setCustomStringProperty('userAgent', window.navigator.userAgent);
    Rox.register('', featureFlags);
    await Rox.setup(apiKey, {
      configurationFetchedHandler,
      freeze: 'none' as Rox.RoxFlagFreezeLevel.None,
    });
  } catch (err) {
    console.warn(err);
  }
};

export function getFeatureFlagsSnapshot(): FeatureFlags {
  return Object.keys(DEFAULT_FEATURE_FLAGS).reduce(
    (snapshot: Partial<FeatureFlags>, flagName: keyof FeatureFlags) => {
      const flag = featureFlags[flagName];

      if (flag instanceof Rox.Flag) {
        return { ...snapshot, [flagName]: flag.isEnabled() };
      }

      if (
        flag instanceof Rox.RoxNumber ||
        // Using `as any` to prevent TS from complaining that flag is `never`,
        // because for now we haven't implemented a RoxString in FeatureFlags
        (flag as any) instanceof Rox.RoxString
      ) {
        return { ...snapshot, [flagName]: flag.getValue() };
      }

      return snapshot;
    },
    {},
  ) as FeatureFlags;
}
